import React, { useState, useEffect } from "react";
import Header from "../../commons/Header/Header";
import Sidebar from "../../commons/Sidebar/Sidebar";
import editImage from "../../assets/images/icons/edit.svg";
import { handleApiCall } from "../../apis";
import { endpoints } from "../../utils/endpoints";
import { toast } from "react-toastify";
import { Button } from "react-bootstrap";

const FEES_FIELDS = [
  { key: "service_fee", label: "Service Fees", groupTitle: "Platform Fees ( % )", suffix: "%" },
  { key: "vat", label: "Vat", groupTitle: "Tax ( % )", suffix: "%" },
  { key: "initial_miles", label: "Initial Miles", groupTitle: "Driver Search Radius", suffix: "" },
  { key: "internal_miles", label: "Internal Miles", groupTitle: null, suffix: "" },
  { key: "base_delivery_charge", label: "Base Delivery Charge", groupTitle: "Delivery Charge", prefix: "$" },
  { key: "order_modify_accept", label: "Order Modify Accept", groupTitle: "Order Modify", suffix: "" },
  { key: "order_modify_deadline", label: "Order Modify Deadline", groupTitle: null, suffix: "" },
  {
    key: "max_distance_restaurant_miles",
    label: "Max Distance Restaurant",
    groupTitle: "Order Assignment",
    suffix: " miles",
  },
  {
    key: "minutes_before_driver_assign",
    label: "Minutes Before Driver Assign",
    groupTitle: null,
    suffix: " min",
  },
  {
    key: "time_difference_to_assign_batch_order",
    label: "Time Difference To Assign Batch Order",
    groupTitle: null,
    suffix: " min",
  },
];

const ADMIN_FIELDS = ["admin_standard_commission", "admin_discounted_commission"];

const DRIVER_FIELDS = [
  "internal_driver_admin_commission",
  "internal_driver_driver_charge",
  "external_driver_fulltime",
  "external_driver_fulltime_10_increment",
  "external_driver_fulltime_admin_commission",
  "external_driver_parttime",
  "external_driver_parttime_10_increment",
  "external_driver_parttime_admin_commission",
  "external_driver_b2b",
  "external_driver_b2b_10_increment",
  "external_driver_b2b_admin_commission",
];

const SECTION_FIELDS = {
  admin: ADMIN_FIELDS,
  driver: DRIVER_FIELDS,
  fees: FEES_FIELDS.map((f) => f.key),
};

const PERCENT_FIELD_LABELS = {
  admin_standard_commission: "Standard Commission",
  admin_discounted_commission: "Discounted Commission",
  internal_driver_admin_commission: "Internal Driver Admin Commission",
  internal_driver_driver_charge: "Internal Driver Charge",
  service_fee: "Service Fees",
  vat: "Vat",
  external_driver_fulltime_admin_commission: "Full Time Admin Commission",
  external_driver_parttime_admin_commission: "Part Time Admin Commission",
  external_driver_b2b_admin_commission: "B2B Admin Commission",
};

const FIELD_LABELS = {
  admin_standard_commission: "Standard Commission",
  admin_discounted_commission: "Discounted Commission",
  internal_driver_admin_commission: "Internal Driver Admin Commission",
  internal_driver_driver_charge: "Internal Driver Charge",
  external_driver_fulltime: "Full Time",
  external_driver_fulltime_10_increment: "Full Time Every 10 % Increament",
  external_driver_fulltime_admin_commission: "Full Time Admin Commission",
  external_driver_parttime: "Part Time",
  external_driver_parttime_10_increment: "Part Time Every 10 % Increament",
  external_driver_parttime_admin_commission: "Part Time Admin Commission",
  external_driver_b2b: "B2B",
  external_driver_b2b_10_increment: "B2B Every 10 % Increament",
  external_driver_b2b_admin_commission: "B2B Admin Commission",
  service_fee: "Service Fees",
  vat: "Vat",
  initial_miles: "Initial Miles",
  internal_miles: "Internal Miles",
  base_delivery_charge: "Base Delivery Charge",
  order_modify_accept: "Order Modify Accept",
  order_modify_deadline: "Order Modify Deadline",
  max_distance_restaurant_miles: "Max Distance Restaurant",
  minutes_before_driver_assign: "Minutes Before Driver Assign",
  time_difference_to_assign_batch_order: "Time Difference To Assign Batch Order",
};

const extractGlobalParameterData = (response) => {
  const payload = response?.data?.data;

  if (Array.isArray(payload)) return payload[0] || {};
  if (Array.isArray(payload?.data)) return payload.data[0] || {};
  if (payload?.data && typeof payload.data === "object") return payload.data;
  if (payload?.result && typeof payload.result === "object") return payload.result;
  if (payload && typeof payload === "object") return payload;

  return {};
};

const SECTION_SAVE_ENDPOINTS = {
  admin: {
    method: "patch",
    endpointKey: "updateGlobalParameterCommission",
  },
  driver: {
    method: "post",
    endpointKey: "addGlobalParameterDriverCharge",
  },
  fees: {
    method: "post",
    endpointKey: "addGlobalParameterOtherDetail",
  },
};

const normalizePayloadValue = (value) => {
  if (value === "") return "";
  const numericValue = Number(value);
  return Number.isNaN(numericValue) ? value : numericValue;
};

const buildSectionPayload = (keys, values) =>
  keys.reduce((payload, key) => {
    payload[key] = normalizePayloadValue(values[key] ?? "");
    return payload;
  }, {});

const getRequiredFieldErrors = (keys, values) =>
  keys.reduce((errors, key) => {
    if (String(values[key] ?? "").trim() === "") {
      errors[key] = `${FIELD_LABELS[key] || "Field"} is required`;
    }
    return errors;
  }, {});

const getInvalidPercentageField = (payload) =>
  Object.entries(PERCENT_FIELD_LABELS).find(([key]) => {
    if (payload[key] === undefined || payload[key] === "") return false;
    const value = Number(payload[key]);
    return !Number.isNaN(value) && value > 100;
  });

const clampPercentageInput = (key, value) => {
  if (!PERCENT_FIELD_LABELS[key] || value === "") return value;

  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) return value;

  return numericValue > 100 ? "100" : value;
};

const formatDisplayValue = (value, prefix = "", suffix = "") => {
  if (value === undefined || value === null || value === "") {
    return "-";
  }
  return `${prefix}${value}${suffix}`;
};

const getParamInputClass = (sectionId, editingSection) =>
  editingSection === sectionId ? "global-param-input-edit" : "global-param-input-view";

function DriverSubSection({ title, children }) {
  return (
    <div className="px-3 my-3">
      <h6 className="mb-3">{title}</h6>
      {children}
    </div>
  );
}

function GlobalParamInput({
  sectionId,
  fieldKey,
  prefix = "",
  suffix = "",
  editingSection,
  getInputValue,
  getParamInputClass,
  onDraftChange,
  error,
}) {
  const isFieldEditing = editingSection === sectionId;

  return (
    <div className="global-param-input-wrap">
      <div className="global-param-input-control">
        {isFieldEditing && prefix && <span className="global-param-input-symbol global-param-input-prefix">{prefix}</span>}
        <input
          type="text"
          className={`${getParamInputClass(sectionId, editingSection)} ${
            isFieldEditing && prefix ? "global-param-input-with-prefix" : ""
          } ${isFieldEditing && suffix ? "global-param-input-with-suffix" : ""} ${
            isFieldEditing && error ? "global-param-input-error" : ""
          }`}
          readOnly={!isFieldEditing}
          value={getInputValue(fieldKey, prefix, suffix, sectionId)}
          onChange={(e) => onDraftChange(fieldKey, e.target.value)}
        />
        {isFieldEditing && suffix && <span className="global-param-input-symbol global-param-input-suffix">{suffix}</span>}
      </div>
      {isFieldEditing && error && <div className="global-param-error-text">{error}</div>}
    </div>
  );
}

function DriverFieldRow({
  label,
  fieldKey,
  prefix,
  suffix,
  editingSection,
  getInputValue,
  getParamInputClass,
  onDraftChange,
  fieldErrors,
}) {
  return (
    <div className="row g-3 mb-2">
      <div className="col-md-3">
        <label className="form-control d-flex align-items-center bg-light mb-0 global-param-static-label">
          {label} <span className="text-danger">*</span>
        </label>
      </div>
      <div className="col-md-3">
        <GlobalParamInput
          sectionId="driver"
          fieldKey={fieldKey}
          prefix={prefix}
          suffix={suffix}
          editingSection={editingSection}
          getInputValue={getInputValue}
          getParamInputClass={getParamInputClass}
          onDraftChange={onDraftChange}
          error={fieldErrors?.[fieldKey]}
        />
      </div>
    </div>
  );
}

function SectionEditHeader({ title, isEditing, onEdit, onSave, onCancel, saving = false }) {
  return (
    <div className="d-flex align-items-center mb-3">
      <h5 className="section-title text-white mb-0">{title}</h5>
      <div className="ml-auto">
        {isEditing ? (
          <>
            <Button variant="primary" size="sm" className="rounded-lg mr-2" onClick={onSave} disabled={saving}>
              {saving ? "Saving..." : "Save"}
            </Button>
            <Button variant="light" size="sm" className="rounded-lg" onClick={onCancel} disabled={saving}>
              Cancel
            </Button>
          </>
        ) : (
          <button type="button" className="btn btn-link p-0 border-0" onClick={onEdit}>
            <img src={editImage} alt="Edit" height="20" />
          </button>
        )}
      </div>
    </div>
  );
}

function GlobalParameter() {
  const [loading, setLoading] = useState(false);
  const [globalParams, setGlobalParams] = useState({});
  const [editingSection, setEditingSection] = useState(null);
  const [savingSection, setSavingSection] = useState(null);
  const [draft, setDraft] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});

  const getGlobalParameter = async () => {
    setLoading(true);
    try {
      const responses = await Promise.allSettled([
        handleApiCall("get", endpoints.getGlobalParameters),
        handleApiCall("get", endpoints.getGlobalParameterDriverCharge),
        handleApiCall("get", endpoints.getGlobalParameterOtherDetail),
      ]);

      const mergedParams = responses.reduce((acc, result) => {
        if (result.status !== "fulfilled") return acc;

        const response = result.value;
        if (response?.data?.code === 200 || response?.data?.success) {
          return {
            ...acc,
            ...extractGlobalParameterData(response),
          };
        }

        return acc;
      }, {});

      if (Object.keys(mergedParams).length > 0) {
        setGlobalParams(mergedParams);
      } else {
        toast.error("Failed to fetch parameters");
      }
    } catch (error) {
      toast.error("Error fetching global parameters");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getGlobalParameter();
  }, []);

  const isEditing = (sectionId) => editingSection === sectionId;

  const getInputValue = (key, prefix, suffix, sectionId) => {
    if (isEditing(sectionId)) {
      return draft[key] ?? "";
    }
    return formatDisplayValue(globalParams[key], prefix, suffix);
  };

  const handleEditSection = (sectionId) => {
    const keys = SECTION_FIELDS[sectionId];
    if (!keys) return;

    const sectionDraft = {};
    keys.forEach((key) => {
      const val = globalParams[key];
      sectionDraft[key] = val !== undefined && val !== null ? String(val) : "";
    });

    setDraft(sectionDraft);
    setFieldErrors({});
    setEditingSection(sectionId);
  };

  const handleCancelEdit = () => {
    setEditingSection(null);
    setDraft({});
    setFieldErrors({});
  };

  const handleSaveSection = async () => {
    const sectionId = editingSection;
    const keys = SECTION_FIELDS[sectionId];
    const saveConfig = SECTION_SAVE_ENDPOINTS[sectionId];

    if (!keys || !saveConfig) return;

    const requiredErrors = getRequiredFieldErrors(keys, draft);
    if (Object.keys(requiredErrors).length > 0) {
      setFieldErrors(requiredErrors);
      return;
    }

    const payload = buildSectionPayload(keys, draft);
    const invalidPercentageField = getInvalidPercentageField(payload);

    if (invalidPercentageField) {
      toast.error(`${invalidPercentageField[1]} cannot be greater than 100%`);
      return;
    }

    setSavingSection(sectionId);
    try {
      const response = await handleApiCall(
        saveConfig.method,
        endpoints[saveConfig.endpointKey],
        payload
      );

      if (response?.data?.code === 200 || response?.data?.code === 201 || response?.data?.success) {
        setGlobalParams((prev) => ({ ...prev, ...payload }));
        setEditingSection(null);
        setDraft({});
        setFieldErrors({});
        toast.success(response?.data?.message || "Parameters saved successfully");
      }
    } catch (error) {
      toast.error("Failed to save parameters");
    } finally {
      setSavingSection(null);
    }
  };

  const handleDraftChange = (key, value) => {
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setDraft((prev) => ({ ...prev, [key]: clampPercentageInput(key, value) }));
  };

  return (
    <div className="container-scroller">
      <Header />
      <div className="container-fluid page-body-wrapper">
        <Sidebar />
        <div className="main-panel">
          <div className="content-wrapper">
            <div className="heading mb-3 d-flex align-items-center">
              <h3 className="text-default weight-400 my-0">Global Parameter</h3>
            </div>

            {/* Admin Commission */}
            <div>
              <SectionEditHeader
                title="Admin Commission ( % )"
                isEditing={isEditing("admin")}
                onEdit={() => handleEditSection("admin")}
                onSave={handleSaveSection}
                onCancel={handleCancelEdit}
                saving={savingSection === "admin"}
              />
              <div className="row g-3">
                <div className="col-md-3">
                  <label className="form-control disabled d-flex align-items-center disable bg-light">
                    Standard Commission <span className="text-danger">*</span>
                  </label>
                </div>
                <div className="col-md-3">
                  <GlobalParamInput
                    sectionId="admin"
                    fieldKey="admin_standard_commission"
                    suffix="%"
                    editingSection={editingSection}
                    getInputValue={getInputValue}
                    getParamInputClass={getParamInputClass}
                    onDraftChange={handleDraftChange}
                    error={fieldErrors.admin_standard_commission}
                  />
                </div>
              </div>
              <div className="row g-3">
                <div className="col-md-3">
                  <label className="form-control disabled d-flex align-items-center disable bg-light">
                    Discounted Commission <span className="text-danger">*</span>
                  </label>
                </div>
                <div className="col-md-3">
                  <GlobalParamInput
                    sectionId="admin"
                    fieldKey="admin_discounted_commission"
                    suffix="%"
                    editingSection={editingSection}
                    getInputValue={getInputValue}
                    getParamInputClass={getParamInputClass}
                    onDraftChange={handleDraftChange}
                    error={fieldErrors.admin_discounted_commission}
                  />
                </div>
              </div>
            </div>

            {/* Driver Charges */}
            <div className="border-top mt-4 pt-4">
              <SectionEditHeader
                title="Driver Charges And Commission ( Amount )"
                isEditing={isEditing("driver")}
                onEdit={() => handleEditSection("driver")}
                onSave={handleSaveSection}
                onCancel={handleCancelEdit}
                saving={savingSection === "driver"}
              />
              <DriverSubSection title="Internal Driver">
                <DriverFieldRow
                  label="Admin Commission"
                  fieldKey="internal_driver_admin_commission"
                  prefix=""
                  suffix="%"
                  editingSection={editingSection}
                  getInputValue={getInputValue}
                  getParamInputClass={getParamInputClass}
                  onDraftChange={handleDraftChange}
                  fieldErrors={fieldErrors}
                />
                <DriverFieldRow
                  label="Driver Charge"
                  fieldKey="internal_driver_driver_charge"
                  prefix=""
                  suffix="%"
                  editingSection={editingSection}
                  getInputValue={getInputValue}
                  getParamInputClass={getParamInputClass}
                  onDraftChange={handleDraftChange}
                  fieldErrors={fieldErrors}
                />
              </DriverSubSection>

              <hr />

              <DriverSubSection title="External Driver">
                <DriverFieldRow
                  label="Full Time"
                  fieldKey="external_driver_fulltime"
                  prefix="$"
                  suffix=""
                  editingSection={editingSection}
                  getInputValue={getInputValue}
                  getParamInputClass={getParamInputClass}
                  onDraftChange={handleDraftChange}
                  fieldErrors={fieldErrors}
                />
                <DriverFieldRow
                  label="Every 10 % Increament"
                  fieldKey="external_driver_fulltime_10_increment"
                  prefix="€"
                  suffix=""
                  editingSection={editingSection}
                  getInputValue={getInputValue}
                  getParamInputClass={getParamInputClass}
                  onDraftChange={handleDraftChange}
                  fieldErrors={fieldErrors}
                />
                <DriverFieldRow
                  label="Admin Commission"
                  fieldKey="external_driver_fulltime_admin_commission"
                  prefix=""
                  suffix="%"
                  editingSection={editingSection}
                  getInputValue={getInputValue}
                  getParamInputClass={getParamInputClass}
                  onDraftChange={handleDraftChange}
                  fieldErrors={fieldErrors}
                />
                <div className="mt-4">
                  <DriverFieldRow
                    label="Part Time"
                    fieldKey="external_driver_parttime"
                    prefix="$"
                    suffix=""
                    editingSection={editingSection}
                    getInputValue={getInputValue}
                    getParamInputClass={getParamInputClass}
                    isEditing={isEditing}
                    onDraftChange={handleDraftChange}
                    fieldErrors={fieldErrors}
                  />
                  <DriverFieldRow
                    label="Every 10 % Increament"
                    fieldKey="external_driver_parttime_10_increment"
                    prefix="€"
                    suffix=""
                    editingSection={editingSection}
                    getInputValue={getInputValue}
                    getParamInputClass={getParamInputClass}
                    isEditing={isEditing}
                    onDraftChange={handleDraftChange}
                    fieldErrors={fieldErrors}
                  />
                  <DriverFieldRow
                    label="Admin Commission"
                    fieldKey="external_driver_parttime_admin_commission"
                    prefix=""
                    suffix="%"
                    editingSection={editingSection}
                    getInputValue={getInputValue}
                    getParamInputClass={getParamInputClass}
                    isEditing={isEditing}
                    onDraftChange={handleDraftChange}
                    fieldErrors={fieldErrors}
                  />
                </div>
              </DriverSubSection>

              <hr />

              <DriverSubSection title="External Driver">
                <DriverFieldRow
                  label="B2B"
                  fieldKey="external_driver_b2b"
                  prefix="$"
                  suffix=""
                  editingSection={editingSection}
                  getInputValue={getInputValue}
                  getParamInputClass={getParamInputClass}
                  onDraftChange={handleDraftChange}
                  fieldErrors={fieldErrors}
                />
                <DriverFieldRow
                  label="Every 10 % Increament"
                  fieldKey="external_driver_b2b_10_increment"
                  prefix="€"
                  suffix=""
                  editingSection={editingSection}
                  getInputValue={getInputValue}
                  getParamInputClass={getParamInputClass}
                  onDraftChange={handleDraftChange}
                  fieldErrors={fieldErrors}
                />
                <DriverFieldRow
                  label="Admin Commission"
                  fieldKey="external_driver_b2b_admin_commission"
                  prefix=""
                  suffix="%"
                  editingSection={editingSection}
                  getInputValue={getInputValue}
                  getParamInputClass={getParamInputClass}
                  onDraftChange={handleDraftChange}
                  fieldErrors={fieldErrors}
                />
              </DriverSubSection>
            </div>

            {/* Platform Fees, Tax, Driver Search Radius — single section, one edit */}
            <div className="border-top mt-4 pt-4">
              <div className="d-flex justify-content-end mb-2">
                {isEditing("fees") ? (
                  <>
                    <Button
                      variant="primary"
                      size="sm"
                      className="rounded-lg mr-2"
                      onClick={handleSaveSection}
                      disabled={savingSection === "fees"}
                    >
                      {savingSection === "fees" ? "Saving..." : "Save"}
                    </Button>
                    <Button
                      variant="light"
                      size="sm"
                      className="rounded-lg"
                      onClick={handleCancelEdit}
                      disabled={savingSection === "fees"}
                    >
                      Cancel
                    </Button>
                  </>
                ) : (
                  <button type="button" className="btn btn-link p-0 border-0" onClick={() => handleEditSection("fees")}>
                    <img src={editImage} alt="Edit" height="20" />
                  </button>
                )}
              </div>
              {FEES_FIELDS.map((field, index) => {
                const showGroupTitle =
                  field.groupTitle &&
                  (index === 0 || FEES_FIELDS[index - 1].groupTitle !== field.groupTitle);

                return (
                  <React.Fragment key={field.key}>
                    {showGroupTitle && (
                      <div className="row g-3 my-3 px-3 mt-4">
                        <div className="col-md-12">
                          <h6 className="mb-0">{field.groupTitle}</h6>
                        </div>
                      </div>
                    )}
                    <div className="row g-3 my-3 px-3">
                      <div className="col-md-3 d-flex align-items-center">
                        <h6 className="mb-0">{field.label}</h6>
                      </div>
                      <div className="col-md-9">
                        <div className="row">
                          <div className="col-md-2 px-0">
                            <GlobalParamInput
                              sectionId="fees"
                              fieldKey={field.key}
                              prefix={field.prefix || ""}
                              suffix={field.suffix || ""}
                              editingSection={editingSection}
                              getInputValue={getInputValue}
                              getParamInputClass={getParamInputClass}
                              onDraftChange={handleDraftChange}
                              error={fieldErrors[field.key]}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default GlobalParameter;
